//
// ChatQuestion - Component for displaying question messages in chat
// Shows italicized questions/statements with author information and controls
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { memo } from 'react';

import {
  Column,
  Row,
} from '../../../display';

import ChatMessageControls from './ChatMessageControls';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  MessageFrame,
  MessageText,
} from './components';

const ChatQuestion = memo(({
  registerChild,
  translate,
  style,
  message,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  visibleInThisGathering = true,
  grpGpid,
  grpLbl,
  grpNbr,
  groupsMap,
  imagesMap,
  prtnrLogoUrl,
  selectGathering,
}) => {
  const {
    id,
    gthrId,
    qstnTxt,
    msgRdatim1,
    qstnId,
  } = message;

  const messageContent = `${translate(qstnId, 'questionnairePrompts')}${(qstnTxt) ? ` ${qstnTxt}` : ''}`;

  const content = (
    <MessageText
      key="txt"
      message={messageContent}
      author={author}
      italic
    />
  );

  return (
    <div
      ref={registerChild}
      key={id}
      className={classNames(
        'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
        {
          'px-6': visibleInThisGathering,
          'px-2 opacity-50': !visibleInThisGathering,
        },
      )}
      style={style}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      <ChatAuthorName
        translate={translate}
        author={author}
        initials={initials}
        crBlocked={crBlocked}
        gcatCode={gcatCode}
        grpNbr={grpNbr}
        prtnrLogoUrl={prtnrLogoUrl}
      />

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        <ChatAuthorIcon
          author={author}
          initials={initials}
          color={color}
          gcatCode={gcatCode}
          grpGpid={grpGpid}
          grpLbl={grpLbl}
          groupsMap={groupsMap}
          imagesMap={imagesMap}
        />

        <MessageFrame
          key={id}
          author={author}
          visibleInThisGathering={visibleInThisGathering}
        >
          {content}
        </MessageFrame>

        <Column
          className={classNames(
            'self-start',
            {
              'order-2': !author,
              'order-4': author,
            },
          )}
          gap={1}
          crossStart={author}
          crossEnd={!author}
        >
          <ChatDate
            dtime={msgRdatim1}
            myMessage={!author}
          />

          <ChatMessageControls
            translate={translate}
            message={message}
            author={author}
          />
        </Column>

      </Row>
    </div>
  );
});
ChatQuestion.displayName = 'ChatQuestion';

ChatQuestion.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  visibleInThisGathering: PropTypes.bool,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  selectGathering: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gthrId: PropTypes.number,
    msgRdatim1: PropTypes.string.isRequired,
    qstnId: PropTypes.string.isRequired,
    qstnTxt: PropTypes.string,
  }).isRequired,
  prtnrLogoUrl: PropTypes.string,
};

export default ChatQuestion;
